import { useState } from 'react';
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const ComponentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'nowrap',
  margin: theme.spacing(0, 6, 0, 3)
}));
const NameFields = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr min-content min-content',
  '& .MuiTextField-root:first-of-type': {
    marginRight: theme.spacing(1)
  }
}));

export type IndividualProfileEditNameProps = {
  className?: string;
  firstName: string;
  lastName: string;
  isLoading?: boolean;
  onNameChange: (firstName: string, lastName: string) => void;
};

export default function IndividualProfileEditName(
  props: Readonly<IndividualProfileEditNameProps>
) {
  const { 
    className,
    firstName, 
    lastName, 
    isLoading,
    onNameChange, 
  } = props;

  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [firstNameDraft, setFirstNameDraft] = useState<string>(firstName);
  const [lastNameDraft, setLastNameDraft] = useState<string>(lastName);


  return (
    <ComponentContainer className={className}>
      {!isEditingName && (
        <Typography variant="h4">
          {isLoading ? 'Loading...' : `${firstName} ${lastName}`}
        </Typography>
      )}
      {isEditingName && (
        <NameFields>
          <TextField
            id="firstName"
            variant="outlined"
            size="small"
            slotProps={{ inputLabel: { shrink: Boolean(firstName) } }}
            label="First Name"
            autoComplete='given-name'
            value={firstNameDraft}
            onChange={(event) => setFirstNameDraft(event.target.value)}
          />
          <TextField
            id="lastName"
            variant="outlined"
            size="small"
            slotProps={{ inputLabel: { shrink: Boolean(lastName) } }}
            label="Last Name"
            autoComplete='family-name'
            value={lastNameDraft}
            onChange={(event) => setLastNameDraft(event.target.value)}
          />
          <Tooltip title="Accept Change">
            <span>
              <IconButton
                onClick={() => {
                  setIsEditingName(false);
                  onNameChange(firstNameDraft, lastNameDraft);
                }}
                color='success'
                disabled={
                  isLoading || 
                  (firstNameDraft.trim().length === 0 && lastNameDraft.trim().length === 0) || 
                  (firstNameDraft === firstName && lastNameDraft === lastName)
                }
                size="medium"
                aria-label='accept change'
              >
                <CheckIcon />
              </IconButton>
            </span>
          </Tooltip>
           <Tooltip title="Cancel">
            <span>
              <IconButton 
                onClick={() => {
                  setIsEditingName(false);
                  setFirstNameDraft(firstName);
                  setLastNameDraft(lastName);
                }}
                color="error"
                disabled={isLoading}
                aria-label="cancel"
                size="medium"
                edge="end"
              >
                <CloseIcon />
              </IconButton>
            </span>
          </Tooltip>
        </NameFields>
      )}
      {!isEditingName && (
        <Tooltip title="Edit Name">
          <span>
            <IconButton 
              onClick={() => setIsEditingName(true)} 
            >
              <EditIcon color="primary" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </ComponentContainer>
  );
}
